import {
  Alert,
  Card,
  CardContent,
  Chip,
  Skeleton,
  Typography,
} from "@mui/material";
import React, { useEffect } from "react";
import { getformatDateFromISOFormat } from "../Utils/utils";
import TicketDetails from "../../interfaces/ticket";
import axiosConfig from "../../axiosConfig";
import CommentTimeline from "../Comment/CommentTimeline";
import { useAppSelector } from "../../app/hooks";

const OrderDetails: React.FC<{
  orderTicketId?: string;
}> = ({ orderTicketId }) => {
  const [orderDetails, setOrderDetails] = React.useState<TicketDetails>();
  const accountInfo = useAppSelector((state) => state.accountInfo);
  // 協力会社（外注先）には顧客を特定できる情報と金額を開示しない。
  const isSubcontractor = accountInfo.type === "subcontractor";

  const fetchOrderDetails = async () => {
    const res = await axiosConfig.get(`/dashboard/tickets/${orderTicketId}`);
    setOrderDetails(res.data.body);
  };

  useEffect(() => {
    fetchOrderDetails();
  }, []);

  if (!orderDetails) {
    return (
      <div className="flex flex-col" style={{ marginBottom: "200px" }}>
        <div
          style={{
            padding: "20px",
            width: "60vw",
            margin: "auto",
          }}
        >
          <Skeleton
            variant="text"
            width="60%"
            height={50}
            style={{ marginTop: "20px" }}
          />
          <Skeleton variant="text" width="60%" height={30} />
          <Skeleton variant="text" width="80%" height={30} />
          <Skeleton variant="text" width="90%" height={30} />
          <Skeleton variant="text" width="50%" height={30} />
          <Skeleton
            variant="text"
            width="60%"
            height={50}
            style={{ marginTop: "20px" }}
          />
          <Skeleton variant="text" width="60%" height={30} />
          <Skeleton variant="text" width="80%" height={30} />
        </div>
      </div>
    );
  }

  return (
    <div className="flex flex-col" style={{ marginBottom: "0px" }}>
      {orderDetails?.is_confirmed === false && (
        <Alert
          severity="warning"
          icon={false}
          sx={{ width: "60vw", margin: "8px auto 0" }}
        >
          <Chip
            label="未発注"
            color="error"
            size="small"
            sx={{ marginRight: 1 }}
          />
          ご発注前のお見積りです
        </Alert>
      )}
      <InfoCard title="注文情報" width={"60vw"}>
        <Typography>
          <strong>発注ID:</strong> {orderDetails?.order_id}
        </Typography>
        <Typography>
          <strong>案件名:</strong> {orderDetails?.project_name}
        </Typography>
        <Typography>
          <strong>見積番号:</strong> {orderDetails?.estimation_number}
        </Typography>
        {/* 協力会社には顧客情報・金額を表示しない */}
        {!isSubcontractor && (
          <>
            <Typography>
              <strong>作成日:</strong>{" "}
              {getformatDateFromISOFormat(orderDetails?.created_at ?? "")}
            </Typography>
            <Typography>
              <strong>会社名:</strong> {orderDetails?.company_name}
            </Typography>
            <Typography>
              <strong>住所:</strong> {orderDetails?.address}
            </Typography>
            <Typography>
              <strong>住所2:</strong> {orderDetails?.address2}
            </Typography>
            <Typography>
              <strong>郵便番号:</strong> {orderDetails?.postal_code}
            </Typography>
            <Typography>
              <strong>Email:</strong> {orderDetails?.email}
            </Typography>
            <Typography>
              <strong>電話番号:</strong> {orderDetails?.phone_number}
            </Typography>
            <Typography>
              <strong>氏名:</strong> {orderDetails?.pic}
            </Typography>
            <Typography>
              <strong>小計:</strong> {orderDetails?.sub_total}
            </Typography>
            <Typography>
              <strong>消費税:</strong> {orderDetails?.consumption_tax}
            </Typography>
            <Typography>
              <strong>合計:</strong> {orderDetails?.total_amount}
            </Typography>
            <Typography>
              <strong>備考:</strong> {orderDetails?.remarks || "-"}
            </Typography>
          </>
        )}
      </InfoCard>

      {!isSubcontractor && (
        <InfoCard title="顧客情報" width={"60vw"}>
          <Typography>
            <strong>顧客ID:</strong> {orderDetails?.customer_id}
          </Typography>
          <Typography>
            <strong>顧客名:</strong> {orderDetails?.customer_name}
          </Typography>
        </InfoCard>
      )}

      {orderDetails?.order_id && (
        <Card
          className="mx-auto mt-4"
          sx={{
            border: 1,
            borderColor: "grey.300",
            borderRadius: "10px",
            width: "60vw",
          }}
        >
          <CardContent>
            <CommentTimeline
              orderId={orderDetails.order_id}
              // 投稿先セレクタには案件配下の全図面を並べる。
              // order_tickets が無い場合（旧APIレスポンス）は表示中の図面のみ。
              tickets={
                orderDetails.order_tickets &&
                orderDetails.order_tickets.length > 0
                  ? orderDetails.order_tickets
                  : orderDetails.ticket_id && orderDetails.file_name
                  ? [
                      {
                        id: orderDetails.ticket_id,
                        file_name: orderDetails.file_name,
                      },
                    ]
                  : []
              }
            />
          </CardContent>
        </Card>
      )}
    </div>
  );
};

const InfoCard: React.FC<{
  title: string;
  children: React.ReactNode;
  width?: string;
}> = ({ title, children, width }) => {
  return (
    <Card
      className="mx-auto mt-4"
      sx={{
        border: 1,
        borderColor: "grey.300",
        borderRadius: "10px",
        width: width ?? "90vw",
      }}
    >
      <CardContent
        sx={{
          "& > *": {
            marginBottom: 1,
          },
        }}
      >
        <Typography
          sx={{
            fontSize: 24,
            fontWeight: "bold",
            marginBottom: 1,
          }}
        >
          {title}
        </Typography>
        {children}
      </CardContent>
    </Card>
  );
};

export default OrderDetails;
